import { Alert, LoadingOverlay, Modal, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { Job } from "@labeling-tools/ui-components/src/utils/helpers";
import { useBenchmarkQuizMetrics } from "@labeling-tools/api/lebron/react-query-hooks";
import { QuizMetricCard } from "./QuizMetricCard";

export interface QuizMetricsModalProps {
  /** The quiz to show. `null` closes the modal and fetches nothing. */
  quizId: string | null;
  onClose: () => void;
}

/**
 * A finished quiz's results.
 *
 * Owns its own fetch, so a caller only has to hold "which quiz is open" — the
 * query stays disabled until a quiz is actually selected. The card gets no
 * `onBack`: the modal's own close control is the only dismissal.
 */
export function QuizMetricsModal({ quizId, onClose }: QuizMetricsModalProps) {
  const { data, isLoading, error } = useBenchmarkQuizMetrics(quizId);

  return (
    <Modal
      opened={!!quizId}
      onClose={onClose}
      title={<Text fw={600}>Quiz results</Text>}
      centered
      size="lg"
      // The whole recipe this app's other portaled modals use verbatim — see
      // PlanogramSearchModal, CoverageTask, CoverageBetaSkipBtn. It has to
      // portal to body, because Layout gives <main> a marginLeft for the
      // sidebar and a modal inside that subtree centers on the content area
      // rather than the viewport. Something here also leaves Modal's inner
      // element unpositioned, so without the explicit fixed/inset override the
      // overlay renders and the content never appears.
      zIndex={10000}
      withinPortal
      portalProps={{ target: "body" }}
      lockScroll={false}
      styles={{
        overlay: { position: "fixed", inset: 0 },
        inner: { position: "fixed", inset: 0 },
      }}
    >
      <div className="relative min-h-[200px]">
        <LoadingOverlay visible={isLoading} zIndex={100} />
        {error && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={16} />}
          >
            {error.message}
          </Alert>
        )}
        {data && (
          <QuizMetricCard
            title="Quiz complete"
            status={data.status as Job["status"]}
            subtitle={`${data.store_name} · ${new Date(
              data.created_at,
            ).toLocaleString()}`}
            precision={data.precision}
            correct={data.correct}
            answered={data.answered}
            avgSecondsPerTask={data.avg_seconds_per_task}
            tasksPerHour={data.tasks_per_hour}
          />
        )}
      </div>
    </Modal>
  );
}
